import React, { useState, useEffect } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import {
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import {
  getBillingManagers,
  deleteBillingManager,
  createBillingManager,
} from "./dashboardSlice";
import { IAppState } from "../../helpers/store";
import { useTranslation, Trans } from "react-i18next";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import { useAppDispatch } from "../app/appHooks";
import DeleteBillingManagerDialog from "./DeleteBillingManagerDialog";

// Component styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    emailList: {
      width: "100%",
      backgroundColor: theme.palette.background.paper,
    },
    pageTitle: {
      color: theme.palette.dark.main,
      fontWeight: "bold",
      fontSize: "1.5rem",
      paddingBottom: theme.spacing(7),
    },
    pageDescription: {
      color: theme.palette.dark.main,
      fontSize: "0.9rem",
      fontWeight: "bold",
      paddingBottom: theme.spacing(2.5),
      width: "90%",
      lineHeight: "1.25rem",
    },
    listItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      "&:hover": {
        backgroundColor: "transparent",
      },
      padding: theme.spacing(0.75, 0),
    },
    removeButton: {
      color: theme.palette.dark.light,
      "&:hover": {
        backgroundColor: "transparent",
      },
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.dark.light}`,
      padding: 0,
      minWidth: "18px",
      minHeight: "18px",
      marginRight: theme.spacing(2),
    },
    iconButton: {
      fontSize: "16px",
    },
    primaryButton: {
      color: theme.palette.light.main,
      backgroundColor: "transparent",
      border: `1px solid ${theme.palette.light.main}`,
      padding: 0,
      minWidth: "18px",
      minHeight: "18px",
      marginRight: theme.spacing(2),
    },
    emailText: {
      color: theme.palette.dark.main,
      fontWeight: "bold",
      fontSize: "0.9rem",
    },
    primaryText: {
      color: theme.palette.gray.main,
      fontWeight: "normal",
      marginLeft: theme.spacing(0.5),
    },
    emailInput: {
      display: "flex",
      alignItems: "center",
      borderBottom: `1px solid ${theme.palette.text.primary}`,
      paddingBottom: theme.spacing(1),
    },
    input: {
      border: "none",
      outline: "none",
      width: "300px",
      borderBottom: `1px solid ${theme.palette.text.primary}`,
      fontSize: "inherit",
      fontFamily: "inherit",
      background: "transparent",
    },
    inputContainer: {
      display: "flex",
      alignItems: "center",
    },
    addButton: {
      height: "22px",
      marginLeft: theme.spacing(2),
    },
    addText: {
      fontSize: "0.9rem",
    },
    underline: {
      textDecoration: "underline",
    },
  })
);

export default function BillingManagers() {
  /* ---- Hooks ---- */
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [isAddingManager, setIsAddingManager] = useState(false);
  const [isBannerOpen, setBannerOpen] = useState(false);
  const [deletedEmail, setDeletedEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const billingManagers = useSelector(
    (state: IAppState) => state.dashboardState.billingManagers
  );

  const subscriptionData = useSelector(
    (state: IAppState) => state.dashboardState.subscriptionData
  );

  const primaryUser = subscriptionData.find(
    (item) => item.is_primary_user === true
  );

  useEffect(() => {
    const fetchBillingManagers = async () => {
      setIsLoading(true);
      await dispatch(getBillingManagers());
      setIsLoading(false);
    };

    fetchBillingManagers();
  }, [dispatch]);

  const handleAddNewManager = () => {
    setIsAddingManager(true);
  };

  const handleCloseInputForm = () => {
    setIsAddingManager(false);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setEmail(value);
  };
  const openBanner = (email: string) => {
    setDeletedEmail(email);
    setBannerOpen(true);
  };

  const closeBanner = () => {
    setBannerOpen(false);
  };

  const handleRemove = (email: string) => {
    dispatch(deleteBillingManager(email));
    setBannerOpen(false);
  };

  const handleCreateNewBillingManager = () => {
    dispatch(createBillingManager(email)).then(() => {
      setEmail("");
      setIsAddingManager(false);
    });
  };

  const allPrimary = subscriptionData.every(
    (item) => item.is_primary_user === true
  );

  return (
    <React.Fragment>
      <Typography className={classes.pageTitle}>
        {t("billing_managers")}
      </Typography>
      {!isLoading && (
        <List className={classes.emailList}>
          {!primaryUser && (
            <Typography className={classes.pageDescription}>
              {t("authorized_user_description")}
            </Typography>
          )}
          {primaryUser && (
            <div>
              <Typography className={classes.pageDescription}>
                {allPrimary ? (
                  t("billing_manager_description")
                ) : (
                  <Trans
                    i18nKey="alt_billing_manager_description"
                    components={{
                      underline: <span className={classes.underline} />,
                    }}
                  />
                )}
              </Typography>
              <ListItem className={classes.listItem}>
                <Button className={classes.primaryButton} disabled>
                  <RemoveIcon className={classes.iconButton} />
                </Button>
                <ListItemText>
                  <Typography className={classes.emailText}>
                    {primaryUser?.primary_email}
                    <span className={classes.primaryText}> {t("primary")}</span>
                  </Typography>
                </ListItemText>
              </ListItem>
            </div>
          )}

          {billingManagers.map((manager, index) => (
            <ListItem key={index} className={classes.listItem}>
              <Button
                className={classes.removeButton}
                onClick={() => openBanner(manager.user_email)}
              >
                <RemoveIcon className={classes.iconButton} />
              </Button>
              <ListItemText>
                <Typography className={classes.emailText}>
                  {manager.user_email}
                </Typography>
              </ListItemText>
            </ListItem>
          ))}
          {primaryUser && (
            <ListItem className={classes.listItem}>
              {isAddingManager ? (
                <Grid container>
                  <Grid item>
                    <Button
                      className={classes.removeButton}
                      onClick={handleCloseInputForm}
                    >
                      <CloseIcon className={classes.iconButton} />
                    </Button>
                  </Grid>
                  <Grid item>
                    <div className={classes.inputContainer}>
                      <input
                        type="text"
                        value={email}
                        onChange={handleEmailChange}
                        className={classes.input}
                        placeholder={t("enter_email")}
                      />
                      <Button
                        className={classes.addButton}
                        onClick={handleCreateNewBillingManager}
                        color="primary"
                        variant="contained"
                      >
                        <Typography className={classes.addText}>
                          {t("add")}
                        </Typography>
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              ) : (
                <Button
                  className={classes.removeButton}
                  onClick={handleAddNewManager}
                >
                  <AddIcon className={classes.iconButton} />
                </Button>
              )}
            </ListItem>
          )}
        </List>
      )}

      <DeleteBillingManagerDialog
        toggle={isBannerOpen}
        email={deletedEmail}
        positiveAction={() => handleRemove(deletedEmail)}
        negativeAction={closeBanner}
        onClose={closeBanner}
      />
    </React.Fragment>
  );
}
